import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ShoppingCart, Heart, User, Menu, X, Search, ChevronDown, LogOut, Package, Settings, Scale } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useCart } from '../context/CartContext';
import { useCompare } from '../context/CompareContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '../components/ui/sheet';
import { AuthModal } from './AuthModal';

export const Header = () => {
  const { user, logout, isAdmin } = useAuth();
  const { getCartItemCount } = useCart();
  const { compareItems } = useCompare();
  const navigate = useNavigate();
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/products?search=${encodeURIComponent(searchQuery)}`);
      setSearchQuery('');
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const categories = [
    { name: 'Cold Pressed Oils', href: '/products?category=cat-oils' },
    { name: 'Millets', href: '/products?category=cat-millets' },
    { name: 'Desi Ghee', href: '/products?category=cat-ghee' },
    { name: 'Organic Rice', href: '/products?category=cat-rice' },
    { name: 'Spices', href: '/products?category=cat-spices' },
    { name: 'Flours', href: '/products?category=cat-flours' },
    { name: 'Dals & Pulses', href: '/products?category=cat-pulses' },
    { name: 'Honey & Jaggery', href: '/products?category=cat-honey' },
  ];

  return (
    <>
      <header className="sticky top-0 z-50 glass border-b border-stone-200">
        {/* Top bar */}
        <div className="bg-primary text-primary-foreground py-2">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <p className="text-center text-sm font-medium">
              Free delivery on orders above ₹2000 within 5 kms
            </p>
          </div>
        </div>

        {/* Main header */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16 lg:h-20">
            {/* Mobile menu button */}
            <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
              <SheetTrigger asChild className="lg:hidden">
                <Button variant="ghost" size="icon">
                  <Menu className="h-6 w-6" />
                </Button>
              </SheetTrigger>
              <SheetContent side="left" className="w-80">
                <SheetHeader>
                  <SheetTitle className="font-serif text-primary">Menu</SheetTitle>
                </SheetHeader>
                <nav className="mt-6 space-y-4">
                  <Link to="/" className="block py-2 text-lg font-medium hover:text-primary" onClick={() => setMobileMenuOpen(false)}>
                    Home
                  </Link>
                  <div className="py-2">
                    <p className="text-sm font-semibold text-muted-foreground mb-2">Categories</p>
                    {categories.map((cat) => (
                      <Link
                        key={cat.name}
                        to={cat.href}
                        className="block py-2 pl-4 text-stone-600 hover:text-primary"
                        onClick={() => setMobileMenuOpen(false)}
                      >
                        {cat.name}
                      </Link>
                    ))}
                  </div>
                  <Link to="/blog" className="block py-2 text-lg font-medium hover:text-primary" onClick={() => setMobileMenuOpen(false)}>
                    Blog
                  </Link>
                  <Link to="/about" className="block py-2 text-lg font-medium hover:text-primary" onClick={() => setMobileMenuOpen(false)}>
                    About Us
                  </Link>
                  <Link to="/contact" className="block py-2 text-lg font-medium hover:text-primary" onClick={() => setMobileMenuOpen(false)}>
                    Contact
                  </Link>
                </nav>
              </SheetContent>
            </Sheet>

            {/* Logo */}
            <Link to="/" className="flex items-center gap-3">
              <img
                src="/logosrisuktam.jpg"
                alt="SriSuktam Organics"
                className="h-12 lg:h-14 w-auto"
              />
              <div className="hidden sm:block">
                <h1 className="font-serif text-xl lg:text-2xl font-semibold text-primary">SriSuktam Organics</h1>
                <p className="text-xs text-muted-foreground italic">Swasthya Shuddi..</p>
              </div>
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden lg:flex items-center gap-8">
              <Link to="/" className="text-stone-700 hover:text-primary font-medium transition-colors">
                Home
              </Link>
              <DropdownMenu>
                <DropdownMenuTrigger className="flex items-center gap-1 text-stone-700 hover:text-primary font-medium transition-colors">
                  Products <ChevronDown className="h-4 w-4" />
                </DropdownMenuTrigger>
                <DropdownMenuContent className="w-56">
                  <DropdownMenuItem asChild>
                    <Link to="/products" className="w-full">All Products</Link>
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  {categories.map((cat) => (
                    <DropdownMenuItem key={cat.name} asChild>
                      <Link to={cat.href} className="w-full">{cat.name}</Link>
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
              <Link to="/blog" className="text-stone-700 hover:text-primary font-medium transition-colors">
                Blog
              </Link>
              <Link to="/about" className="text-stone-700 hover:text-primary font-medium transition-colors">
                About
              </Link>
              <Link to="/contact" className="text-stone-700 hover:text-primary font-medium transition-colors">
                Contact
              </Link>
            </nav>

            {/* Right actions */}
            <div className="flex items-center gap-2 lg:gap-4">
              {/* Search */}
              <form onSubmit={handleSearch} className="hidden md:flex items-center">
                <div className="relative">
                  <Input
                    type="text"
                    placeholder="Search products..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-48 lg:w-64 pr-10 bg-stone-50/50"
                  />
                  <Button type="submit" variant="ghost" size="icon" className="absolute right-0 top-0">
                    <Search className="h-4 w-4 text-muted-foreground" />
                  </Button>
                </div>
              </form>

              {/* Wishlist */}
              {user && (
                <Link to="/wishlist">
                  <Button variant="ghost" size="icon" className="relative">
                    <Heart className="h-5 w-5" />
                  </Button>
                </Link>
              )}

              {/* Compare */}
              <Link to="/compare">
                <Button variant="ghost" size="icon" className="relative" data-testid="compare-button">
                  <Scale className="h-5 w-5" />
                  {compareItems.length > 0 && (
                    <span className="absolute -top-1 -right-1 h-5 w-5 rounded-full bg-accent text-stone-900 text-xs flex items-center justify-center">
                      {compareItems.length}
                    </span>
                  )}
                </Button>
              </Link>

              {/* Cart */}
              <Link to="/cart">
                <Button variant="ghost" size="icon" className="relative" data-testid="cart-button">
                  <ShoppingCart className="h-5 w-5" />
                  {getCartItemCount() > 0 && (
                    <span className="absolute -top-1 -right-1 h-5 w-5 rounded-full bg-secondary text-white text-xs flex items-center justify-center">
                      {getCartItemCount()}
                    </span>
                  )}
                </Button>
              </Link>

              {/* User menu */}
              {user ? (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="icon" data-testid="user-menu-button">
                      <User className="h-5 w-5" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-56">
                    <div className="px-2 py-1.5">
                      <p className="font-medium">{user.name}</p>
                      <p className="text-sm text-muted-foreground">{user.email}</p>
                    </div>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem asChild>
                      <Link to="/account" className="w-full flex items-center gap-2">
                        <User className="h-4 w-4" /> My Account
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuItem asChild>
                      <Link to="/orders" className="w-full flex items-center gap-2">
                        <Package className="h-4 w-4" /> My Orders
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuItem asChild>
                      <Link to="/wishlist" className="w-full flex items-center gap-2">
                        <Heart className="h-4 w-4" /> Wishlist
                      </Link>
                    </DropdownMenuItem>
                    {isAdmin() && (
                      <>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem asChild>
                          <Link to="/admin" className="w-full flex items-center gap-2">
                            <Settings className="h-4 w-4" /> Admin Dashboard
                          </Link>
                        </DropdownMenuItem>
                      </>
                    )}
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={handleLogout} className="text-destructive cursor-pointer">
                      <LogOut className="h-4 w-4 mr-2" /> Logout
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              ) : (
                <Button
                  onClick={() => setShowAuthModal(true)}
                  className="bg-primary hover:bg-primary/90 text-white rounded-full px-6"
                  data-testid="login-button"
                >
                  Login
                </Button>
              )}
            </div>
          </div>
        </div>
      </header>

      <AuthModal open={showAuthModal} onOpenChange={setShowAuthModal} />
    </>
  );
};
